import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Router} from "@angular/router";
import {MediaMatcher} from "@angular/cdk/layout";
import {ManufacturingSetService} from "../../service-data/manufacturing-set.service";
import {PackageService} from "../../service-data/package.service";
import {LoggedInCallback} from "../../service-auth/cognito.service";
import {EventHandler} from "../../table/EventHandler";
import {RoleChangeListener, StartupService} from "../../service-ui/startup.service";
import {IssueService} from "../../service-data/issue.service";
import {PackageTypeService} from "../../service-data/package-type.service";
import {PrintNodeOrderService} from "../../service-data/print-node-order.service";
import {ProductService} from "../../service-data/product.service";
import {FacilityInventoryProductService} from "../../service-data/facility-inventory-product.service";
import {ManufacturingTaskBucketPeriodService} from "../../service-data/manufacturing-task-bucket-period.service";
import {ManufacturingTaskBucketService} from "../../service-data/manufacturing-task-bucket.service";
import {PrintNodeOrderItemService} from "../../service-data/print-node-order-item.service";
import {OrderItemService} from "../../service-data/order-item.service";
import {NotificationsService} from "../../service-data/notifications.service";
import {ManufacturingResourceService} from "../../service-data/manufacturing-resource.service";
import {FacilityInvoiceService} from "../../service-data/facility-invoice.service";
import {Observable, Subject} from "rxjs";
import {FacilityService} from "../../service-data/facility.service";
import {OrganizationInvoiceService} from "../../service-data/organization-invoice.service";
import {OrganizationService} from "../../service-data/organization.service";
import {DraftService} from "../../service-data/draft.service";
import {AddressService} from "../../service-data/address-service";
import {ChargeProfileService} from "../../service-data/charge-profile-service";
import {ProjectService} from "../../service-data/project.service";
import {EcommerceConnectionService} from "../../service-data/ecommerce-connection.service";
import {EcommerceOrderService} from "../../service-data/ecommerce-order.service";
import {EcommerceProductService} from "../../service-data/ecommerce-product.service";
import {DataService} from "../../service-data/data-service";
import {AbstractLiveDataService} from "../../service-data/abstract-live-data.service";
import {PaperTypeService} from "../../service-data/paper-type.service";
import {UserService} from "../../service-data/user.service";

@Component({
  selector: 'app-startup',
  templateUrl: './startup.component.html',
  styleUrls: ['./startup.component.css']
})

export class StartupComponent implements OnDestroy, OnInit, LoggedInCallback, AfterViewInit, EventHandler, RoleChangeListener {

  eventHandler: EventHandler;
  mobileQuery: MediaQueryList;
  private _mobileQueryListener: () => void;
  onDestroySubject: Subject<boolean> = new Subject();

  expandAttachmentRequests: boolean = false;

  isLoaded: boolean = false;

  /**
   * Drives the repair affordance on the `accountSetupIncomplete` panel.
   * FAILED carries `repairErrorMessage`, which holds the server's own
   * sentence rather than a generic one where we have it.
   */
  repairState: 'IDLE' | 'REPAIRING' | 'FAILED' = 'IDLE';
  repairErrorMessage: string = '';

  constructor(public router: Router,
              public startupService: StartupService,
              public notificationsService: NotificationsService,
              public addressService: AddressService,
              public ecommerceConnectionService: EcommerceConnectionService,
              public ecommerceProductService: EcommerceProductService,
              public ecommerceOrderService: EcommerceOrderService,
              public chargeProfileService: ChargeProfileService,
              public manufacturingResourceService: ManufacturingResourceService,
              public manufacturingSetService: ManufacturingSetService,
              public paperTypeService: PaperTypeService,
              public packageTypeService: PackageTypeService,
              public packageService: PackageService,
              public issueService: IssueService,
              public printNodeOrderService: PrintNodeOrderService,
              public printNodeOrderItemService: PrintNodeOrderItemService,
              public orderItemService: OrderItemService,
              public facilityInventoryProductService: FacilityInventoryProductService,
              public productService: ProductService,
              public projectService: ProjectService,
              public draftService: DraftService,
              public manufacturingTaskBucketPeriodService: ManufacturingTaskBucketPeriodService,
              public manufacturingTaskBucketService: ManufacturingTaskBucketService,
              public organizationInvoiceService: OrganizationInvoiceService,
              public organizationService: OrganizationService,
              public facilityInvoiceService: FacilityInvoiceService,
              public facilityService: FacilityService,
              public changeDetectorRef: ChangeDetectorRef,
              public userService: UserService,
              public media: MediaMatcher) {
    this.startupService.addRoleChangeListener(this);
    this.startupService.touchLastUserActivity();
    this.eventHandler = this;
    this.mobileQuery = media.matchMedia('(max-width: 600px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);
  }

  roleChanged(role: string, roleId: string): void {
    this.getData();
  }

  ngOnInit() {
    this.startupService.isAuthenticated(this);
  }

  ngAfterViewInit(): void {
  }

  isLoggedIn(message: string, isLoggedIn: boolean) {
    if (!isLoggedIn) {
      this.router.navigate(['/home/login']);
    } else {
      this.getData();
    }
  }

  /**
   * Recover an account that signed in but has no role behind it.
   *
   * Two steps, and the second is not optional: the repair writes
   * `custom:roleId` to Cognito, but our token predates that write, so
   * without a refresh a successful repair still looks broken on screen.
   *
   * The refreshed token is the only thing consulted for the verdict.
   * `repaired: false` from the endpoint means "already set up", which is a
   * success, so both response shapes take the same path and let
   * `setJwtToken()` decide. When it selects a role it notifies this
   * component through `roleChanged()`, which starts the data load - so
   * there is nothing to do here on the happy path but stop showing the
   * spinner.
   */
  repairAccount(): void {
    this.repairState = 'REPAIRING';
    this.repairErrorMessage = '';
    this.userService.repairAccount().subscribe({
      next: () => {
        this.startupService.refreshJwtToken().subscribe({
          next: (hasRole: boolean) => {
            if (hasRole) {
              this.repairState = 'IDLE';
            } else {
              // Endpoint reported success but the refreshed token still
              // carries no role. Genuinely unresolved rather than a
              // transport failure, so it needs a human.
              this.repairState = 'FAILED';
              this.repairErrorMessage = '';
            }
          },
          error: (e: any) => {
            this.repairState = 'FAILED';
            this.repairErrorMessage = (e && e.message) ? e.message : '';
          }
        });
      },
      error: (e: any) => {
        this.repairState = 'FAILED';
        this.repairErrorMessage = (e && e.error && e.error.error) ? e.error.error : ((e && e.message) ? e.message : '');
      }
    });
  }
  checkIsLoaded() {
    for ( const serviceName of Object.keys(this.notificationsService.services)) {
      // if ( this.notificationsService.services[serviceName].loadingState !== 'LOADED' && !(this.notificationsService.services[serviceName].loadingState === 'INITIAL' && this.notificationsService.services[serviceName].objects.length > 0 ) ) {
      if ( this.notificationsService.services[serviceName].loadingState !== 'LOADED' && this.notificationsService.services[serviceName].loadingState !== 'INITIAL' ) {
        this.startupService.isDataLoaded = false;
        return;
      }
    }
    this.startupService.isDataLoaded = true;
    if ( this.startupService.isDataLoaded ) {
      // Honor a one-shot post-startup route override if one has
      // been queued (e.g. by an external-callback entry flow such as
      // the Shopify install handoff in AppComponent — see
      // docs/architecture/routing.md "External callback entry
      // points"). The consume helper clears it after returning.
      const override = this.startupService.consumePendingPostStartupRoute();
      if (override) {
        this.router.navigate(override);
        return;
      }
      switch (this.startupService.selectedRole) {
        case 'network': {
          this.router.navigate(['/secure/manufacturingbuckets']);
          break;
        }
        case 'facility': {
          this.router.navigate(['/secure/manufacturingsets']);
          break;
        }
        case 'organization': {
          this.router.navigate(['/secure/projects']);
          break;
        }
        case 'serviceprovider': {
          this.router.navigate(['/secure/orderitems']);
          break;
        }
        default: {
          this.router.navigate(['/secure/orderitems']);
          break;
        }
      }
    }
  }
  getData(): void {
    this.startupService.touchLastUserActivity();
    this.startupService.isDataLoaded = false;

    for ( const serviceName of Object.keys(this.notificationsService.services)) {
      this.notificationsService.services[serviceName].loadingState = 'INITIAL';
    }

    // Network and Facilities
    if ( this.startupService.selectedRole === 'network' ||  this.startupService.selectedRole === 'facility') {
      if ( this.startupService.selectedRole === 'network') {
        const _role = this.startupService.selectedRole;
        const _roldId = this.startupService.selectedRoleId;
        this.facilityService.getItems(true).subscribe(objects => {
          if ( _role === this.startupService.selectedRole && _roldId === this.startupService.selectedRoleId ) {
          }
          this.checkIsLoaded();
          console.log(`Done with Facilities length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        });
      }
      const manufacturingResourceFilter = {};
      this.manufacturingResourceService.getFilteredItems(manufacturingResourceFilter, true, 25).subscribe(objects => {
        this.checkIsLoaded();
        console.log(`Done with ManufacturingResources length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
      });
      const manufacturingTaskBucketFilter = {
        eq: {
          ' manufacturingTaskBucket.status ': 'OPEN',
          ' date_add(date(substring(manufacturingTaskBucketPeriod.period, 2, 10)), INTERVAL 8 DAY) > now() ': 1,
          ' date_add(date(substring(manufacturingTaskBucketPeriod.period, 2, 10)), INTERVAL -8 DAY) < now() ': 1
        }
      };
      this.manufacturingTaskBucketService.getFilteredItems(manufacturingTaskBucketFilter, true, 1).subscribe(objects => {
        this.checkIsLoaded();
        console.log(`Done with ManufacturingTaskBuckets length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
      });

      if ( this.startupService.selectedRole === 'facility') {
        const manufacturingSetsFilter = {
          eq: {
            ' date_add(date(substring(manufacturingTaskBucketPeriod.period, 2, 10)), INTERVAL 24 DAY) > now() ': 1
          },
          gt: {
            'manufacturingSet.ID': 700
          }
        };
        this.manufacturingSetService.getFilteredItems(manufacturingSetsFilter, true, 20).subscribe(objects => {
          this.checkIsLoaded();
          console.log(`Done with ManufacturingSets length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        });
      }
      if ( this.startupService.selectedRole === 'network') {
        const ecommerceConnectionsFilter = {
          eq: {
            ' ecommerceConnection.state = "ACTIVE" AND ecommerceConnection.type in ("SHOPIFY", "WOOCOMMERCE", "CSV") ': 1
          }
        };
        this.ecommerceConnectionService.getFilteredItems(ecommerceConnectionsFilter, true, 25).subscribe(objects => {
          this.checkIsLoaded();
          console.log(`Done with EcommerceConnections length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        });
        const ecommerceOrdersFilter = {
          eq: {
            ' date_add(date(substring(ecommerceOrder.createdDate, 1, 10)), INTERVAL 180 DAY) > now() and ecommerceOrder.gatewayagenttype in ("SHOPIFY", "WOOCOMMERCE", "CSV") ': 1
          }
        };
        this.ecommerceOrderService.getFilteredItems(ecommerceOrdersFilter, true, 2).subscribe(objects => {
          this.checkIsLoaded();
          console.log(`Done with EcommerceOrders length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        });
        const ecommerceProductsFilter = { eq: {'ecommerceConnection.state': 'ACTIVE' }};
        this.ecommerceProductService.getFilteredItems(ecommerceProductsFilter, true, 25).subscribe(objects => {
          this.checkIsLoaded();
          console.log(`Done with EcommerceProducts length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        });

        const manufacturingSetsFilter2 = {
          eq: {
            ' date_add(date(substring(manufacturingTaskBucketPeriod.period, 2, 10)), INTERVAL 8 DAY) > now() ': 1
          },
          gt: {
            'manufacturingSet.ID': 700
          }
        };
        this.manufacturingSetService.getFilteredItems(manufacturingSetsFilter2, true, 20).subscribe(objects => {
          this.checkIsLoaded();
          console.log(`Done with ManufacturingSets length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        });
      }

      const issuesFilter = {
        eq: {
          'issue.status': 'OPEN'
        }
      };
      this.issueService.getFilteredItems(issuesFilter, true, 25).subscribe(objects => {
        this.checkIsLoaded();
        console.log(`Done with Issues length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
      });

      this.packageTypeService.getItems(true, 25).subscribe(objects => {
        this.checkIsLoaded();
        console.log(`Done with PackageTypes length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
      });

      this.paperTypeService.getItems(true, 25).subscribe(objects => {
        this.checkIsLoaded();
        console.log(`Done with PaperTypes length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
      });

      // this.productService.getItems(true, 5).subscribe(products => {
      //   this.checkIsLoaded();
      //   console.log(`Done with Product length: ${products.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
      // });

      this.facilityInventoryProductService.getItems(true, 15).subscribe(objects => {
        this.checkIsLoaded();
        console.log(`Done with FacilityInventoryProduct length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
      });

      if ( this.startupService.selectedRole === 'facility') {
        const packageFilter = {eq: {'((printNodeOrder.shippingStatus = "NONE" AND date_add(printNodeOrder.created, INTERVAL 30 DAY) > now() ) OR date_add(printNodeOrder.created, INTERVAL 15 DAY) > now())': 1}};
        this.packageService.getFilteredItems(packageFilter, true, 10).subscribe(objects => {
          this.checkIsLoaded();
          console.log(`Done with Packages length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        });
      }

      const printNodeOrderFilter = {
        eq: {'((printNodeOrder.shippingStatus = "NONE" AND date_add(printNodeOrder.created, INTERVAL 30 DAY) > now() ) OR date_add(printNodeOrder.created, INTERVAL 15 DAY) > now())': 1}
        // ne: { 'printNodeOrder.ID': 286249 }
      };
      this.printNodeOrderService.getFilteredItems(printNodeOrderFilter, true, 10).subscribe(objects => {
        this.checkIsLoaded();
        console.log(`Done with PrintNodeOrders length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
      });

      const facilityInvoiceFilter = {};
      this.facilityInvoiceService.getFilteredItems(facilityInvoiceFilter, true, 15).subscribe(objects => {
        this.checkIsLoaded();
        console.log(`Done with FacilityInvoices length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
      });
    }

    // Service Provider and Organizations
    if ( this.startupService.selectedRole === 'organization' ||  this.startupService.selectedRole === 'serviceProvider') {
      const issuesFilter = {
        eq: {
          'issue.status': 'OPEN'
        }
      };
      this.issueService.getFilteredItems(issuesFilter, true, 25).subscribe(issues => {
        this.checkIsLoaded();
        console.log(`Done with Issues length: ${issues.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
      });

      this.packageTypeService.getItems(true, 25).subscribe(packageTypes => {
        this.checkIsLoaded();
        console.log(`Done with PackageTypes length: ${packageTypes.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
      });

      this.paperTypeService.getItems(true, 25).subscribe(objects => {
        this.checkIsLoaded();
        console.log(`Done with PaperTypes length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
      });

      if ( this.startupService.selectedRole === 'organization') {
        const ecommerceConnectionsFilter = {
          eq: {
            ' ecommerceConnection.state = "ACTIVE" AND ecommerceConnection.type in ("SHOPIFY", "WOOCOMMERCE", "CSV") ': 1
          }
        };

        this.facilityInventoryProductService.getItems(true, 15).subscribe(objects => {
          this.checkIsLoaded();
          console.log(`Done with FacilityInventoryProduct length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        });

        this.ecommerceConnectionService.getFilteredItems(ecommerceConnectionsFilter, true, 25).subscribe(objects => {
          this.checkIsLoaded();
          console.log(`Done with EcommerceConnections length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        });
        const ecommerceOrdersFilter = {
          eq: {
            ' date_add(date(substring(ecommerceOrder.createdDate, 1, 10)), INTERVAL 365 DAY) > now() ': 1
          }
        };
        this.ecommerceOrderService.getFilteredItems(ecommerceOrdersFilter, true, 10).subscribe(objects => {
          this.checkIsLoaded();
          console.log(`Done with EcommerceOrders length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        });
        const ecommerceProductsFilter = { eq: {'ecommerceConnection.state': 'ACTIVE' }};
        this.ecommerceProductService.getFilteredItems(ecommerceProductsFilter, true, 25).subscribe(objects => {
          this.checkIsLoaded();
          console.log(`Done with EcommerceProducts length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        });
        this.projectService.getItems(true, 25). subscribe(objects => {
          this.checkIsLoaded();
          console.log(`Done with Project length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        });
        this.productService.getItems(true, 25).subscribe(objects => {
          this.checkIsLoaded();
          console.log(`Done with Product length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        });
        this.draftService.getItems(true, 25).subscribe(objects => {
          this.checkIsLoaded();
          console.log(`Done with Draft length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        });
        this.addressService.getItems(true, 100).subscribe(objects => {
          this.checkIsLoaded();
          console.log(`Done with Address length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        });
        this.chargeProfileService.getItems(true, 100).subscribe(objects => {
          this.checkIsLoaded();
          console.log(`Done with ChargeProfiles length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        });
        const packageFilter = {eq: {'((printNodeOrder.shippingStatus = "NONE" AND date_add(printNodeOrder.created, INTERVAL 365*2 DAY) > now() ) OR date_add(printNodeOrder.created, INTERVAL 365*2 DAY) > now())': 1}};
        this.packageService.getFilteredItems(packageFilter, true, 10).subscribe(objects => {
          this.checkIsLoaded();
          console.log(`Done with Packages length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        });

        const printNodeOrderFilter = {eq: {'((printNodeOrder.shippingStatus = "NONE" AND date_add(printNodeOrder.created, INTERVAL 365*2 DAY) > now() ) OR date_add(printNodeOrder.created, INTERVAL 365*2 DAY) > now())': 1}};
        this.printNodeOrderService.getFilteredItems(printNodeOrderFilter, true, 25).subscribe(objects => {
          this.checkIsLoaded();
          console.log(`Done with PrintNodeOrders length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        });
      }
      if ( this.startupService.selectedRole === 'serviceProvider') {
        const packageFilter = {eq: {'((printNodeOrder.shippingStatus = "NONE" AND date_add(printNodeOrder.created, INTERVAL 30 DAY) > now() ) OR date_add(printNodeOrder.created, INTERVAL 10 DAY) > now())': 1}};
        this.packageService.getFilteredItems(packageFilter, true, 25).subscribe(objects => {
          this.checkIsLoaded();
          console.log(`Done with Packages length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        });

        const printNodeOrderFilter = {eq: {'((printNodeOrder.shippingStatus = "NONE" AND date_add(printNodeOrder.created, INTERVAL 30 DAY) > now() ) OR date_add(printNodeOrder.created, INTERVAL 10 DAY) > now())': 1}};
        this.printNodeOrderService.getFilteredItems(printNodeOrderFilter, true, 25).subscribe(objects => {
          this.checkIsLoaded();
          console.log(`Done with PrintNodeOrders length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        });

        // this.facilityInventoryProductService.getItems(true, 15).subscribe(objects => {
        //   this.checkIsLoaded();
        //   console.log(`Done with FacilityInventoryProduct length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
        // });
      }

      const organizationFilter = {
        eq: {
          'state': 'ACTIVE'
        },
      };
      this.organizationService.getFilteredItems(organizationFilter, true, 50).subscribe(objects => {
        this.checkIsLoaded();
        console.log(`Done with Organizations length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
      });
      const organizationInvoiceFilter = {
        eq: {
          ' ( date_add(organizationInvoice.periodStart, INTERVAL 90 DAY) > now() ) ': 1
        },
        ne: {
          'organizationInvoice.amount': 0
        }
      };
      this.organizationInvoiceService.getFilteredItems(organizationInvoiceFilter, true, 50).subscribe(objects => {
        this.checkIsLoaded();
        console.log(`Done with OrganizationInvoices length: ${objects.length} role: ${this.startupService.selectedRole}-${this.startupService.selectedRoleId}`);
      });
    }
  }


  ngOnDestroy(): void {
    this.mobileQuery.removeListener(this._mobileQueryListener);
    this.onDestroySubject.next(true);
    this.onDestroySubject.unsubscribe();
  }


  //
  // public redirectToDetails = (id: string) => {
  //
  // }
  //
  // public redirectToUpdate = (id: string) => {
  //
  // }
  //
  // public redirectToDelete = (id: string) => {
  //
  // }

  public event(action: string, data: any, event) {

  }
  getKeys(object: any) {
    return Object.keys(object);
  }

  getLoadedCount(): number {
    let count = 0;
    for (const key of Object.keys(this.notificationsService.services)) {
      if (this.notificationsService.services[key].loadingState === 'LOADED') { count++; }
    }
    return count;
  }

  getLoadingCount(): number {
    let count = 0;
    for (const key of Object.keys(this.notificationsService.services)) {
      if (this.notificationsService.services[key].loadingState === 'LOADING') { count++; }
    }
    return count;
  }

  getErrorCount(): number {
    let count = 0;
    for (const key of Object.keys(this.notificationsService.services)) {
      if (this.notificationsService.services[key].loadingState === 'ERROR') { count++; }
    }
    return count;
  }

  getOverallProgress(): number {
    const keys = Object.keys(this.notificationsService.services);
    const active = keys.filter(k => this.notificationsService.services[k].loadingState !== 'INITIAL');
    if (active.length === 0) { return 0; }
    const loaded = active.filter(k => this.notificationsService.services[k].loadingState === 'LOADED' || this.notificationsService.services[k].loadingState === 'ERROR');
    return (loaded.length / active.length) * 100;
  }

  refresh () {
    this.getData();
  }

  reload(dataService: DataService) {
    if ( dataService.implementsReload()) {
      const dataServiceX: AbstractLiveDataService<any> = dataService as AbstractLiveDataService<any>;
      dataServiceX.reload().subscribe( result => {
        this.checkIsLoaded();
        console.log(`Reload getItems() complete for ${dataServiceX.getLoggingObjectTypeName()}`);
      });
    }
  }
}
